import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { StatusChip } from '@/components/ui/Chip';
import { Field } from '@/components/ui/Field';
import { placeAnchoredMenu } from '@/components/ui/placeAnchoredMenu';
import type { QuestionnaireFormChoice } from '@/lib/patientReports/questionnaireFormOptions';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

/** Same cap as the old inline list (`max-h-64`). The menu scrolls inside it. */
const MENU_MAX_HEIGHT = 256;
/** About three option rows, after the gap. Below this the list would leave the screen. */
const MENU_MIN_HEIGHT = 120;

/**
 * Closed control plus a menu on document.body.
 *
 * The previous list stayed open inside the section card. That card is a
 * sortable with overflow:hidden. Focusing the list on click scrolled the
 * patient page, so the pointer missed the option and the choice never stuck.
 * The menu is portaled, and mousedown on an option does not move focus.
 * It opens downward, like the other dropdowns, and flips up only when less
 * than a short slice of the list would fit under the button.
 */
export function QuestionnaireFormPicker({
  sectionId,
  value,
  choices,
  missing,
  disabled,
  onChange,
}: {
  sectionId: string;
  value: string;
  choices: readonly QuestionnaireFormChoice[];
  missing: boolean;
  disabled?: boolean;
  onChange: (id: string) => void;
}) {
  const { t } = useTranslation();
  const reactId = useId();
  const listId = `questionnaire-${sectionId || reactId}`;
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    placement: 'top' | 'bottom';
  } | null>(null);
  const filled = choices.filter((choice) => choice.filled);
  const rest = choices.filter((choice) => !choice.filled);
  const optionIds = ['', ...choices.map((choice) => choice.id)];
  const selected = choices.find((choice) => choice.id === value) ?? null;
  const buttonLabel = selected?.name || t('patientReports.form_none');

  const close = (focusTrigger: boolean) => {
    setOpen(false);
    if (focusTrigger) buttonRef.current?.focus({ preventScroll: true });
  };

  const pick = (id: string) => {
    if (disabled) return;
    onChange(id);
    close(true);
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      close(true);
    };
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const trigger = buttonRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) return;
      const rect = trigger.getBoundingClientRect();
      const width = Math.max(rect.width, 240);
      const placed = placeAnchoredMenu({
        trigger: rect,
        menuWidth: width,
        menuHeight: Math.min(menu.scrollHeight, MENU_MAX_HEIGHT),
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        align: 'start',
        gap: 4,
        prefer: 'bottom',
        minHeight: MENU_MIN_HEIGHT,
      });
      setBox({
        top: placed.top,
        left: placed.left,
        width,
        maxHeight: Math.min(MENU_MAX_HEIGHT, placed.maxHeight),
        placement: placed.placement,
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, choices]);

  const onButtonKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      setOpen(true);
    }
  };

  const move = (event: ReactKeyboardEvent<HTMLDivElement>, direction: 1 | -1) => {
    event.preventDefault();
    event.stopPropagation();
    const index = Math.max(0, optionIds.indexOf(value));
    const next = optionIds[index + direction];
    if (next === undefined) return;
    onChange(next);
    const menu = menuRef.current;
    const option = menu?.querySelector<HTMLElement>(`[data-value="${cssEscape(next)}"]`);
    if (menu && option) revealInMenu(menu, option);
  };

  const menu =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            id={listId}
            role="listbox"
            tabIndex={-1}
            aria-labelledby={`${listId}-label`}
            aria-activedescendant={optionDomId(listId, value)}
            data-testid="report-questionnaire-options"
            data-placement={box?.placement ?? 'bottom'}
            className="overflow-y-auto rounded-ctl border border-ds-line bg-ds-card shadow-pop focus-visible:outline-none"
            style={{
              position: 'fixed',
              top: box?.top ?? 0,
              left: box?.left ?? 0,
              width: box?.width,
              maxHeight: box?.maxHeight ?? MENU_MAX_HEIGHT,
              visibility: box ? 'visible' : 'hidden',
              zIndex: 80,
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') move(event, 1);
              else if (event.key === 'ArrowUp') move(event, -1);
            }}
            onMouseDown={(event) => {
              // Keep the pointer on the option. Focusing this menu would scroll
              // the patient page and drop the click.
              if (event.target === event.currentTarget) return;
              event.preventDefault();
            }}
          >
            <PickerOption
              listId={listId}
              id=""
              name={t('patientReports.form_none')}
              filled={false}
              selected={value === ''}
              onPick={pick}
            />
            {filled.length > 0 ? <GroupLabel>{t('patientReports.form_filled')}</GroupLabel> : null}
            {filled.map((choice) => (
              <PickerOption
                key={choice.id}
                listId={listId}
                id={choice.id}
                name={choice.name}
                filled
                selected={value === choice.id}
                onPick={pick}
              />
            ))}
            {filled.length > 0 && rest.length > 0 ? (
              <GroupLabel>{t('patientReports.form_other')}</GroupLabel>
            ) : null}
            {rest.map((choice) => (
              <PickerOption
                key={choice.id}
                listId={listId}
                id={choice.id}
                name={choice.name}
                filled={false}
                selected={value === choice.id}
                onPick={pick}
              />
            ))}
          </div>,
          document.body,
        )
      : null;

  return (
    <Field
      label={<span id={`${listId}-label`}>{t('patientReports.form_label')}</span>}
      hint={t('patientReports.form_specific_hint')}
      error={missing ? t('patientReports.form_missing') : undefined}
      hintId={`${listId}-hint`}
      errorId={`${listId}-error`}
    >
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-labelledby={`${listId}-label`}
        aria-invalid={missing || undefined}
        aria-describedby={missing ? `${listId}-error` : `${listId}-hint`}
        data-testid="report-questionnaire-picker"
        className={cn(
          'ds-field flex w-full items-center gap-s2 text-left',
          missing && 'border-[1.5px] border-ds-red',
        )}
        onClick={() => {
          if (disabled) return;
          setOpen((current) => !current);
        }}
        onKeyDown={onButtonKeyDown}
      >
        <span className={cn(TYPE.body, 'min-w-0 flex-1 truncate text-ds-text')}>{buttonLabel}</span>
        {selected?.filled ? (
          <StatusChip tone="completed" className="shrink-0">
            {t('patientReports.form_filled')}
          </StatusChip>
        ) : null}
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-ds-muted', open && 'rotate-180')}
          strokeWidth={1.75}
          aria-hidden
        />
      </button>
      {menu}
    </Field>
  );
}

function GroupLabel({ children }: { children: string }) {
  return (
    <p className={cn(TYPE.meta, 'bg-ds-sunk px-s3 py-s1 text-ds-muted')} role="presentation">
      {children}
    </p>
  );
}

function optionDomId(listId: string, id: string): string {
  return `${listId}-option-${id || 'none'}`;
}

function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  return value.replace(/["\\]/g, '\\$&');
}

function revealInMenu(menu: HTMLElement, option: HTMLElement) {
  const top = option.offsetTop;
  const bottom = top + option.offsetHeight;
  if (top < menu.scrollTop) menu.scrollTop = top;
  else if (bottom > menu.scrollTop + menu.clientHeight) {
    menu.scrollTop = bottom - menu.clientHeight;
  }
}

function PickerOption({
  listId,
  id,
  name,
  filled,
  selected,
  onPick,
}: {
  listId: string;
  id: string;
  name: string;
  filled: boolean;
  selected: boolean;
  onPick: (id: string) => void;
}) {
  const { t } = useTranslation();
  const choose = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onPick(id);
  };
  return (
    <button
      type="button"
      id={optionDomId(listId, id)}
      role="option"
      aria-selected={selected}
      data-value={id}
      data-filled={filled ? 'true' : 'false'}
      className={cn(
        'flex w-full cursor-pointer items-center gap-s2 px-s3 py-s2 text-left',
        selected ? 'bg-ds-teal-tint' : 'hover:bg-ds-sunk',
      )}
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onClick={choose}
    >
      <span className={cn(TYPE.body, 'min-w-0 flex-1 truncate text-ds-text')}>{name}</span>
      {filled ? (
        <StatusChip tone="completed" className="shrink-0">
          {t('patientReports.form_filled')}
        </StatusChip>
      ) : null}
    </button>
  );
}
